import { HeadersBuilder } from "Global/Base/Comunication/HeadersBuilder";
import { ASServices } from "OW.Remoting/Basic/ServiceEndpoints";
import { RequestContext } from "./RequestContext";

/**
 * Parametry, které se vkládají do URL adresy dotazu
 * @includeToDoc
 */
export interface IUrlParams {
    [paramName: string]: any;
}

/**
 * Parametry pro grupování dotazů
 * @includeToDoc
 */
export interface IGroupingParams {
    /** Zda se mají dotazy seskupovat podle názvu funkce dotazu */
    groupByFunction?: boolean;
    /** Zda se mají dotazy seskupovat podle obsahu těla dotazu */
    groupByBody?: boolean;
    /** Nazev atributu v těle dotazu, který identifikuje jednotlivé požadavky od sebe a podle kterého se seskupují */
    groupByBodyAttr?: string;
    /** Název parametru, ve kterém jsou uložené zgrupované položky pro dotaz */
    groupedAttrName?: string;

    /** Název parametru, ve kterém jsou uložené zgrupované položky v odpovědi */
    resultGroupedAttrName?: string;
    /** Název parametru, který identifikuje jednotlivé požadavky od sebe v návratové struktuře */
    resultIDParameterName?: string;
}

/**
 * HTTP hlavičky posílané v dotazech
 * @includeToDoc
 */
export type HeaderMix = { [name: string]: any } | Headers | HeadersBuilder | undefined;

/**
 * Metoda HTTP dotazu
 * @includeToDoc
 */
export type RequestMethod = "GET" | "POST" | "HEAD" | "PUT" | "PATCH" | "DELETE" | "CONNECT" | "OPTIONS" | "TRACE";

/**
 * Základní třída umožňující komunikaci s webovou službou
 * @includeToDoc
 */
export interface ICommunicationManager {
    /**
     * Umožňuje vyvolat nízkoúrovňový HTTP dotaz
     * * Většinou je vhodnější použít vyšší _call*_ API *
     * @param functionName Název funkce na endpointu
     * @param functionBody Tělo dotazu
     * @param requestMethod Metoda HTTP dotazu
     * @param prefixPath Část URL mezi adresou webové služby a funkcí endpointu
     * @param headers HTTP hlavičky k odeslání spolu s dotazem
     * @param requestContext Viz {@link RequestContext}
     */
    jsonRequest(functionName: string, functionBody?: any, requestMethod?: RequestMethod, prefixPath?: string, headers?: HeaderMix, requestContext?: RequestContext): Promise<unknown>;
    /**
     * Vyvolá HTTP dotaz na webovou službu
     * @param functionName Název funkce na endpointu
     * @param service Typ endpointu webové služby
     * @param requestMethod Metoda HTTP dotazu
     * @param urlParameters Objekt s parametry, které se vloží do url pod svými názvy
     * @param functionBody Tělo dotazu
     * @param priority Priorita odeslání dotazu
     * @param groupingParams Parametry pro seskupování více dotazů dohromady
     * @param requestContext Viz {@link RequestContext}
     */
    callRequest(functionName: string, service: ASServices, requestMethod: RequestMethod, urlParameters?: IUrlParams, functionBody?: any, priority?: number, groupingParams?: IGroupingParams, requestContext?: RequestContext): Promise<any>;
    /**
     * Vyvolá HTTP GET dotaz na webovou službu
     * @param functionName Název funkce na endpointu
     * @param service Typ endpointu webové služby
     * @param urlParameters Objekt s parametry, které se vloží do url pod svými názvy
     * @param priority Priorita odeslání dotazu
     * @param requestContext Viz {@link RequestContext}
     */
    callGETRequest(functionName: string, service: ASServices, urlParameters?: IUrlParams, priority?: number, requestContext?: RequestContext): Promise<any>;
    /**
     * Vyvolá HTTP POST dotaz na webovou službu
     * @param functionName Název funkce na endpointu
     * @param service Typ endpointu webové služby
     * @param urlParameters Objekt s parametry, které se vloží do url pod svými názvy
     * @param functionBody Tělo dotazu
     * @param priority Priorita odeslání dotazu
     * @param requestContext Viz {@link RequestContext}
     */
    callPOSTRequest(functionName: string, service: ASServices, urlParameters?: IUrlParams, functionBody?: any, priority?: number, requestContext?: RequestContext): Promise<any>;
    /**
     * Vyvolá HTTP PATCH dotaz na webovou službu
     * @param functionName Název funkce na endpointu
     * @param service Typ endpointu webové služby
     * @param urlParameters Objekt s parametry, které se vloží do url pod svými názvy
     * @param functionBody Tělo dotazu
     * @param headers HTTP hlavičky k odeslání spolu s dotazem
     * @param priority Priorita odeslání dotazu
     * @param requestContext Viz {@link RequestContext}
     */
    callPATCHRequest(functionName: string, service: ASServices, urlParameters?: IUrlParams, functionBody?: any, headers?: HeaderMix, priority?: number, requestContext?: RequestContext): Promise<any>;
    /**
     * Vyvolá seskupovaný HTTP GET dotaz na webovou službu. Manažer sbírá požadavky na stejné volání po určitou dobu a pak je pošle jako jeden dotaz.
     * @param functionName Název funkce na endpointu
     * @param service Typ endpointu webové služby
     * @param urlParameters Objekt s parametry, které se vloží do url pod svými názvy
     * @param priority Priorita odeslání dotazu
     * @param groupingParams Nastavení pro seskupování
     * @param requestContext Viz {@link RequestContext}
     */
    callGroupedGETRequest(functionName: string, service: ASServices, urlParameters?: IUrlParams, priority?: number, groupingParams?: IGroupingParams, requestContext?: RequestContext): Promise<any>;
    /**
     * Vyvolá seskupovaný HTTP POST dotaz na webovou službu. Manažer sbírá požadavky na stejné volání po určitou dobu a pak je pošle jako jeden dotaz.
     * @param functionName Název funkce na endpointu
     * @param service Typ endpointu webové služby
     * @param urlParameters Objekt s parametry, které se vloží do url pod svými názvy
     * @param functionBody Tělo dotazu
     * @param priority Priorita odeslání dotazu
     * @param groupingParams Nastavení pro seskupování
     * @param requestContext Viz {@link RequestContext}
     */
    callGroupedPOSTRequest(functionName: string, service: ASServices, urlParameters?: IUrlParams, functionBody?: any, priority?: number, groupingParams?: IGroupingParams, requestContext?: RequestContext): Promise<any>;

    /**
     * Method waits until all requests with the context ID are done.
     * @param contextId ID of the context.
     */
    waitForContextRequests(contextId: string): Promise<void>;
}

export class ICommunicationManager {
    private static _methods: (keyof ICommunicationManager)[] = [
        "jsonRequest",
        "callRequest",
        "callGETRequest",
        "callPOSTRequest",
        "callPATCHRequest",
        "callGroupedPOSTRequest",
        "callGroupedGETRequest",
        "waitForContextRequests",
    ];
    private static _properties: (keyof ICommunicationManager)[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null
            && !this._properties.some((value) => !Reflect.has(instance, value))
            && !this._methods.some(value => typeof instance[value] !== "function");
    }
}
